import { createSelector } from 'reselect';
import MobileDetect from 'mobile-detect';
import storage from '@xto10x/storage/lib';
import isSignedInWith from '@xto10x/oauth2/lib/getSignedInWith';
import {
  StorageKey,
  UserSubscriptionTypes,
  ConfigCacheKeys
} from 'utils/constants';
import { getCachedConfig, setConfigCache } from 'utils/app.utils';
import { actions as objectiveActions } from 'containers/Objective/store/actions';
import { actions as headerActions } from 'containers/Header/store/actions';
import { compareValues, isNotEmptyString } from '@xto10x/core/lib/app.utils';
import { actions } from './actions';

const deviceDetailsGb = new MobileDetect(navigator.userAgent);
const deviceDetails = {
  mobile: deviceDetailsGb.mobile(),
  tablet: deviceDetailsGb.tablet(),
  os: deviceDetailsGb.os(),
  isSafari: deviceDetailsGb.is('safari')
};

const initialState = {
  isAuthenticated:
    isSignedInWith() !== 'NONE' &&
    !!storage.getDataFromLocal(StorageKey.USER_INFO) &&
    isNotEmptyString(storage.getDataFromLocal(StorageKey.REQUESTER_USER_ID)),
  currentUserInfo: storage.getDataFromLocal(StorageKey.USER_INFO),
  userOrgs: storage.getDataFromLocal(StorageKey.USER_ORGS),
  device: deviceDetails,
  loading: false,
  orgData: null,
  diffOrgData: {},
  integrations: null,
  alert: {
    showAlert: false,
    type: '',
    text: '',
    alertAction: null
  },
  companyTeamsList: [],
  userTeamsData: [],
  companyTagsList: [],
  manageableTagsList: null,
  notificationPreferences: {},
  isDropDownOpen: false,
  highlightedObjective: null,
  tooltipInfo: null,
  isTaskPanelOpen: false,
  RTUEventStream: null,
  isNewAppVersionAvailable: false,
  newVersionAvailableAlert: null,
  okrHierarchyUpdatedAlert: null,
  bottomSheetOpen: false,
  recentlyAddedTeam: null,
  goalStrengthScoreSchema: getCachedConfig(
    ConfigCacheKeys.goalsStrengthSchema,
    StorageKey.GOALS_STRENGTH_SCHEMA
  ),
  isAnnualGoalsSpace: false,
  objectiveConfig: null,
  objectiveOperations: [],
  directReportees: null,
  hierarchy: null,
  accessibleModules: null,
  visibleModules: null,
  orgAccessibleModules: null,
  isAdvancedUser: null,
  isBasicUser: null,
  currentUserViewDetails: null,
  userRoles: { apps: 'USER', okr: 'USER', people: 'USER' },
  rolesUpdated: false,
  newAppRedirectionConfig: null,
  fetchedOKRModuleData: null,
  isUserManager: false,
  isUserLineManager: false,
  isUserHrbp: false,
  fullscreenHandlers: {},
  currentUserHrbpDetails: [],
  currentUserReporteesByPersona: {}
};

export const getGoalStrengthScoreSchema = createSelector(
  state => state.appReducer.goalStrengthScoreSchema,
  goalStrengthScoreSchema => goalStrengthScoreSchema
);

// Memorized  Selector Function
export const getUserOrgsSelector = createSelector(
  state => state.appReducer.userOrgs,
  userOrgs => userOrgs
);

// Memorized  Selector Function
export const getCompanyTeamsSelector = createSelector(
  state => state.appReducer.companyTeamsList,
  companyTeamsList => companyTeamsList
);

export const getCompanyTagsSelector = createSelector(
  state => state.appReducer.companyTagsList,
  companyTagsList => companyTagsList
);

export const getManageableTagsList = createSelector(
  state => state.appReducer.manageableTagsList,
  manageableTagsList => manageableTagsList
);

// Memorized  Selector Function
export const getCurrentUserInfoSelector = createSelector(
  state => state.appReducer.currentUserInfo,
  currentUserInfo => currentUserInfo
);

// Memorized  Selector Function
export const getUserTeamsDataSelector = createSelector(
  state => state.appReducer.userTeamsData,
  userTeamsData => userTeamsData
);

// Memorized  Selector Function
export const getIsAuthenticatedSelector = createSelector(
  state => state.appReducer.isAuthenticated,
  isAuthenticated => isAuthenticated
);

export const getDeviceDetailsSelector = createSelector(
  state => state.appReducer.device,
  device => device
);

// Memorized  Selector Function
export const getLoadingSelector = createSelector(
  state => state.appReducer.loading,
  loading => loading
);

// Memorized  Selector Function
export const getAlertSelector = createSelector(
  state => state.appReducer.alert,
  alert => alert
);

// Memorized  Selector Function
export const getNotificationPreferencesSelector = createSelector(
  state => state.appReducer.notificationPreferences,
  notificationPreferences => notificationPreferences
);

export const getIntegrationsSelector = createSelector(
  state => state.appReducer.integrations,
  integrations => integrations
);

// Memorized  Selector Function
export const getIsDropDownOpenSelector = createSelector(
  state => state.appReducer.isDropDownOpen,
  isDropDownOpen => isDropDownOpen
);

export const getHighlightedObjective = createSelector(
  state => state.appReducer.highlightedObjective,
  highlightedObjective => highlightedObjective
);

export const getTooltipInfo = createSelector(
  state => state.appReducer.tooltipInfo,
  tooltipInfo => tooltipInfo
);

export const getTaskPanelOpenState = createSelector(
  state => state.appReducer.isTaskPanelOpen,
  isTaskPanelOpen => isTaskPanelOpen
);

export const getCurrentRTUEventStream = createSelector(
  state => state.appReducer.RTUEventStream,
  RTUEventStream => RTUEventStream
);

export const getIsNewAppVersionAvailableSelector = createSelector(
  state => state.appReducer.isNewAppVersionAvailable,
  isNewAppVersionAvailable => isNewAppVersionAvailable
);

export const getNewVersionAvailableAlertSelector = createSelector(
  state => state.appReducer.newVersionAvailableAlert,
  newVersionAvailableAlert => newVersionAvailableAlert
);

export const getOKRHierarchyUpdatedAlertSelector = createSelector(
  state => state.appReducer.okrHierarchyUpdatedAlert,
  okrHierarchyUpdatedAlert => okrHierarchyUpdatedAlert
);

export const getIsBottomSheetMobileOpen = createSelector(
  state => state.appReducer.bottomSheetOpen,
  bottomSheetOpen => bottomSheetOpen
);

export const getRecentlyAddedTeamSelector = createSelector(
  state => state.appReducer.recentlyAddedTeam,
  recentlyAddedTeam => recentlyAddedTeam
);

export const getOrgDataSelector = createSelector(
  state => state.appReducer.orgData,
  orgData => orgData
);

export const getIsAnnualGoalsSpace = createSelector(
  state => state.appReducer.isAnnualGoalsSpace,
  isAnnualGoalsSpace => isAnnualGoalsSpace
);

export const getObjectiveConfig = createSelector(
  state => state.appReducer.objectiveConfig,
  objectiveConfig => objectiveConfig
);

export const getObjectiveOperationsList = createSelector(
  state => state.appReducer.objectiveOperationsList,
  objectiveOperationsList => objectiveOperationsList
);

export const getDirectReportees = createSelector(
  state => state.appReducer.directReportees,
  directReportees => directReportees
);

export const getHierarchy = createSelector(
  state => state.appReducer.hierarchy,
  hierarchy => hierarchy
);

export const getOrgAccessibleModules = createSelector(
  state => state.appReducer.orgAccessibleModules,
  orgAccessibleModules => orgAccessibleModules
);

export const getIsAdvancedUser = createSelector(
  state => state.appReducer.isAdvancedUser,
  isAdvancedUser => isAdvancedUser
);

export const getIsBasicUser = createSelector(
  state => state.appReducer.isBasicUser,
  isBasicUser => isBasicUser
);

export const getCurrentUserViewDetails = createSelector(
  state => state.appReducer.currentUserViewDetails,
  currentUserViewDetails => currentUserViewDetails
);

export const getNewAppRedirectionConfig = createSelector(
  state => state.appReducer.newAppRedirectionConfig,
  newAppRedirectionConfig => newAppRedirectionConfig
);

export const getAccessibleModules = createSelector(
  state => state.appReducer.accessibleModules,
  accessibleModules => accessibleModules
);

export const getVisibleModules = createSelector(
  state => state.appReducer.visibleModules,
  visibleModules => visibleModules
);

export const getUserRolesSelector = createSelector(
  state => state.appReducer.userRoles,
  userRoles => userRoles
);

export const getFetchedOKRModuleData = createSelector(
  state => state.appReducer.fetchedOKRModuleData,
  fetchedOKRModuleData => fetchedOKRModuleData
);

export const getIsCurrentUserManager = createSelector(
  state => state.appReducer.isUserManager,
  isUserManager => isUserManager
);

export const getIsCurrentUserLineManager = createSelector(
  state => state.appReducer.isUserLineManager,
  isUserLineManager => isUserLineManager
);

export const getIsCurrentUserHrbp = createSelector(
  state => state.appReducer.isUserHrbp,
  isUserHrbp => isUserHrbp
);

export const getCurrentUserHierarchyLevel = createSelector(
  state => state.appReducer.currentUserHierarchyLevel,
  currentUserHierarchyLevel => currentUserHierarchyLevel
);

export const getCurrentUserHrbpDetailsSelector = createSelector(
  state => state.appReducer.currentUserHrbpDetails,
  currentUserHrbpDetails => currentUserHrbpDetails
);

export const getCurrentUserReporteesByPersonaSelector = createSelector(
  state => state.appReducer.currentUserReporteesByPersona,
  currentUserReporteesByPersona => currentUserReporteesByPersona
);

export const getIsRolesUpdatedSelector = createSelector(
  state => state.appReducer.rolesUpdated,
  rolesUpdated => rolesUpdated
);

export default (state = initialState, payload = {}) => {
  switch (payload.type) {
    case actions.SAVE_USER_INFO_ACTION:
      storage.saveDataInLocal(StorageKey.USER_INFO, payload.userInfo);
      storage.saveDataInLocal(
        StorageKey.REQUESTER_USER_ID,
        payload.userInfo.user_reference_id
      );
      return {
        ...state,
        isAuthenticated: true,
        currentUserInfo: payload.userInfo
      };

    case actions.SAVE_USER_ORGS_ACTION:
      storage.saveDataInLocal(StorageKey.USER_ORGS, payload.userOrgs);
      return {
        ...state,
        userOrgs: payload.userOrgs
      };

    case actions.SAVE_HIGHLIGHTED_OBJECTIVE:
      return {
        ...state,
        highlightedObjective: payload.requestBody,
        blink: payload.blink,
        disableAutoClearHighlightObjective: payload.disableAutoClear
      };

    case actions.CLEAR_HIGHLIGHTED_OBJECTIVE:
      return {
        ...state,
        highlightedObjective: null,
        blink: false,
        disableAutoClearHighlightObjective: false
      };

    case actions.SHOW_APP_LOADING:
      return {
        ...state,
        loading: true
      };

    case actions.HIDE_APP_LOADING:
      return {
        ...state,
        loading: false
      };

    case actions.SHOW_ALERT_MSG:
      return {
        ...state,
        alert: {
          showAlert:
            payload.alertType &&
            (payload.alertType === 'error' ||
              (payload.alertType !== 'error' && payload.alertMessage) ||
              payload.alertAction.showSubSection),
          type: payload.alertType,
          text: payload.alertMessage,
          alertAction: payload.alertAction,
          parseMessage: payload.parseMessage,
          customTime: payload.customTime
        }
      };

    case actions.CLEAR_ALERT_MSG:
      return {
        ...state,
        alert: {
          showAlert: false,
          type: '',
          text: '',
          alertAction: null
        }
      };

    case actions.SAVE_COMPANY_TEAMS_LIST:
      return {
        ...state,
        companyTeamsList: payload.companyTeamsList.sort(compareValues('label'))
      };

    case actions.SAVE_COMPANY_TAGS_LIST:
      return {
        ...state,
        companyTagsList: payload.companyTagsList
      };

    case actions.SAVE_MANAGEABLE_TAGS:
      return {
        ...state,
        manageableTagsList: payload.tagsList
      };

    case actions.SAVE_USER_TEAMS_DATA:
      return {
        ...state,
        userTeamsData: payload.userTeamsData.sort(compareValues('label'))
      };

    case actions.SAVE_INTEGRATIONS_ACTION:
      return {
        ...state,
        integrations: payload.integrations
      };

    case actions.SHOW_CHANGE_ORG_DIALOG:
      return {
        ...state,
        diffOrgData: payload.data
      };
    case actions.SAVE_NOTIFICATION_PREFERENCE:
      return {
        ...state,
        notificationPreferences: payload.data
      };

    case actions.UPDATE_DROPDOWN_STATE:
      return {
        ...state,
        isDropDownOpen: payload.data
      };

    case actions.SAVE_TOOLTIP_INFO:
      return {
        ...state,
        tooltipInfo: payload.data
      };

    case actions.UPDATE_TASK_PANEL_OPEN_STATE:
      return {
        ...state,
        isTaskPanelOpen: payload.state
      };

    case actions.RESET_REDUCER:
      return {
        ...state,
        isAuthenticated: payload.resetIsAuthenticated
          ? false
          : state.isAuthenticated,
        currentUserInfo: {},
        loading: false,
        diffOrgData: {},
        companyTeamsList: [],
        userTeamsData: [],
        notificationPreferences: {},
        isDropDownOpen: false,
        highlightedObjective: null
      };

    case actions.SET_RTU_EVENT_STREAM:
      return {
        ...state,
        RTUEventStream: payload.eventStream
      };

    case actions.IS_NEW_APP_VERSION_AVAILABLE:
      return {
        ...state,
        isNewAppVersionAvailable: payload.value
      };

    case actions.SHOW_NEW_VERSION_AVAILABLE_ALERT_MSG:
      return {
        ...state,
        newVersionAvailableAlert: {
          showAlert:
            payload.alertType &&
            (payload.alertType === 'error' ||
              (payload.alertType !== 'error' && payload.alertMessage)),
          type: payload.alertType,
          text: payload.alertMessage,
          alertAction: payload.alertAction,
          parseMessage: payload.parseMessage,
          customTime: payload.customTime
        }
      };

    case actions.SHOW_OKR_HIERARCHY_ALERT_MESSAGE:
      return {
        ...state,
        okrHierarchyUpdatedAlert: {
          showAlert:
            payload.alertType &&
            (payload.alertType === 'error' ||
              (payload.alertType !== 'error' && payload.alertMessage)),
          type: payload.alertType,
          text: payload.alertMessage,
          alertAction: payload.alertAction,
          parseMessage: payload.parseMessage,
          customTime: payload.customTime
        }
      };

    case headerActions.SAVE_SELECTED_TEAM:
    case headerActions.SAVE_SELECTED_OBJECTIVE_CYCLE:
    case headerActions.SET_FILTERS:
    case headerActions.SET_DASHBOARD_FILTERS:
    case headerActions.SET_SHOW_COMPANY_OBJECTIVES:
    case actions.ENABLE_SEARCH_ACTION:
    case actions.CLEAR_OKR_HIERARCHY_ALERT_MESSAGE:
      return {
        ...state,
        okrHierarchyUpdatedAlert: null
      };

    case objectiveActions.SET_SELECTED_OBJECTIVE:
      return {
        ...state,
        highlightedObjective: null,
        blink: false,
        disableAutoClearHighlightObjective: false
      };

    case actions.MARK_MOBILE_BOTTOM_SHEET_AS_OPEN:
      return {
        ...state,
        bottomSheetOpen: true
      };

    case actions.MARK_MOBILE_BOTTOM_SHEET_AS_CLOSE:
      return {
        ...state,
        bottomSheetOpen: false
      };

    case actions.SAVE_RECENTLY_CREATE_TEAM:
      return {
        ...state,
        recentlyAddedTeam: payload.team_id
      };

    case actions.SAVE_GOAL_STRENGTH_SCORES:
      setConfigCache(
        ConfigCacheKeys.goalsStrengthSchema,
        StorageKey.GOALS_STRENGTH_SCHEMA,
        payload.goalStrengthScoreSchema,
        15
      );
      return {
        ...state,
        goalStrengthScoreSchema: payload.goalStrengthScoreSchema
      };

    case actions.SAVE_ORG_DATA_IN_STATE:
      return {
        ...state,
        orgData: payload.org
      };

    case actions.UPDATE_IS_ANNUAL_GOALS_SPACE:
      return {
        ...state,
        isAnnualGoalsSpace: payload.status
      };

    case actions.SET_OBJECTIVE_CONFIG:
      return {
        ...state,
        objectiveConfig: payload.data
      };
    case actions.SET_OBJECTIVE_OPERATIONS_LIST:
      return {
        ...state,
        objectiveOperationsList: payload.data
      };

    case actions.SET_ORG_DIRECT_REPORTEES:
      return {
        ...state,
        directReportees: payload.directReportees
      };

    case actions.SET_ORG_HIERARCHY:
      return {
        ...state,
        hierarchy: {
          managers: payload.managers,
          reportees: payload.reportees
        }
      };

    case actions.SET_ACCESSIBLE_MODULES:
      return {
        ...state,
        accessibleModules: payload.accessibleModules,
        visibleModules: payload.visibleModules,
        isAdvancedUser: payload.accessibleModules?.includes(
          UserSubscriptionTypes.ADVANCED
        ),
        isBasicUser: payload.accessibleModules?.includes(
          UserSubscriptionTypes.BASIC
        )
      };

    case actions.SET_ORG_ACCESSIBLE_MODULES:
      return {
        ...state,
        orgAccessibleModules: payload.modules
      };

    case actions.SET_CURRENT_USER_VIEW_DETAILS:
      return {
        ...state,
        currentUserViewDetails: payload.view
      };

    case actions.SET_USER_ROLES:
      return {
        ...state,
        userRoles: payload.roles,
        rolesUpdated: true
      };

    case actions.SET_NEW_APP_REDIRECTION_CONFIG:
      return {
        ...state,
        newAppRedirectionConfig: payload.config
      };

    case actions.SET_FETCHED_OKR_MODULE_DATA:
      return {
        ...state,
        fetchedOKRModuleData: payload.state
      };

    case actions.SET_IS_USER_MANAGER:
      return {
        ...state,
        isUserManager: payload.data
      };

    case actions.SET_IS_USER_LINE_MANAGER:
      return {
        ...state,
        isUserLineManager: payload.data
      };

    case actions.SET_IS_USER_HRBP:
      return {
        ...state,
        isUserHrbp: payload.data
      };

    case actions.SET_CURRENT_USER_HIERARCHY_LEVEL:
      return {
        ...state,
        currentUserHierarchyLevel: payload.data
      };

    case actions.SET_FULLSCREEN_HANDLER:
      return {
        ...state,
        fullscreenHandlers: {
          ...state.fullscreenHandlers,
          [payload.state.name]: payload.state.handle
        }
      };

    case actions.SET_CURRENT_USER_HRBP_DETAILS:
      return {
        ...state,
        currentUserHrbpDetails: payload.data
      };

    case actions.SET_CURRENT_USER_REPORTEES_BY_PERSONA:
      return {
        ...state,
        currentUserReporteesByPersona: payload.data
      };

    default:
      return state;
  }
};
